import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Rocket, MessagesSquare, PenLine, Layers, Award, Plane, ArrowRight, Clock, Monitor, ShieldCheck } from 'lucide-react';
import { trackTrialCtaClick } from '../lib/analytics';

const ICONS = [Rocket, MessagesSquare, PenLine, Layers, Award, Plane];

const COPY = {
  es: {
    kicker: 'Perfiles',
    title: '¿Te resulta familiar?',
    lead: '«Entiendo español, pero me cuesta hablar». «Sé gramática, pero mantener una conversación es difícil». «Me da miedo equivocarme cuando hablo». «Llevo tiempo estudiando, pero siento que no avanzo». No necesitas aprender más español «en general»: necesitas trabajar el español que tú necesitas.',
    items: [
      { t: 'Empiezo desde cero', d: 'Si nunca has estudiado el idioma, empezamos con lo esencial para que entiendas y digas tus primeras frases sin bloqueos.' },
      { t: 'Entiendo español, pero me cuesta hablar', d: 'Sesiones centradas en conversación para perder el miedo, ganar fluidez y reaccionar con naturalidad.' },
      { t: 'Sé gramática, pero conversar es difícil', d: 'Explicaciones claras de gramática y vocabulario, con práctica real para que se queden de verdad.' },
      { t: 'Llevo tiempo estudiando, pero no avanzo', d: 'Definimos tu punto de partida y avanzamos paso a paso hacia el siguiente nivel, de A1 a C2.' },
      { t: 'Quiero preparar el DELE', d: 'Como examinadora DELE, te preparo las cuatro pruebas con simulacros y estrategias de examen.' },
      { t: 'Vivo en España y quiero más independencia', d: 'Clases enfocadas en situaciones reales: entrevistas, trámites, reuniones, viajes y vida diaria.' },
    ],
    ctaTitle: 'Tu español puede empezar con una conversación',
    ctaMeta: ['30 minutos', 'Online', 'Sin compromiso'],
    ctaButton: 'Reservar mi clase de prueba gratis',
  },
  en: {
    kicker: 'Who it is for',
    title: 'Who are these lessons for?',
    lead: 'I work with adults from different countries who want to learn at their own pace with close guidance. These are the profiles I work with most:',
    items: [
      { t: 'Complete beginners', d: 'If you have never studied the language, we start with the essentials so you understand and say your first sentences without freezing.' },
      { t: 'People who want to loosen up speaking', d: 'Conversation-focused sessions to lose the fear, gain fluency and react naturally.' },
      { t: 'People who need to tidy up grammar', d: 'Clear grammar and vocabulary explanations with real practice so they actually stick.' },
      { t: 'All levels, from A1 to C2', d: 'We define your starting point and move step by step towards the next level.' },
      { t: 'DELE exam preparation', d: 'As a DELE examiner, I prepare you for all four papers with mock exams and exam strategies.' },
      { t: 'For travelling, working or living in Spain', d: 'Lessons focused on real situations: interviews, paperwork, meetings, travel and daily life.' },
    ],
    ctaTitle: 'Start with a free trial lesson',
    ctaMeta: ['30 minutes', 'Online', 'No commitment'],
    ctaButton: 'Book your free trial lesson',
  },
  de: {
    kicker: 'Für wen',
    title: 'Für wen ist dieser Unterricht?',
    lead: 'Ich arbeite mit Erwachsenen aus dem deutschsprachigen Raum, die in ihrem Tempo und mit persönlicher Begleitung lernen möchten. Das sind die häufigsten Profile:',
    items: [
      { t: 'Anfänger ohne Vorkenntnisse', d: 'Wenn du die Sprache noch nie gelernt hast, starten wir mit dem Wesentlichen, damit du deine ersten Sätze verstehst und sagst.' },
      { t: 'Wer freier sprechen will', d: 'Stunden mit Fokus auf Konversation, um die Hemmungen zu verlieren, flüssiger zu werden und natürlich zu reagieren.' },
      { t: 'Wer Grammatik sortieren muss', d: 'Klare Erklärungen zu Grammatik und Wortschatz mit echter Praxis, damit sie wirklich sitzen.' },
      { t: 'Alle Niveaus, von A1 bis C2', d: 'Wir bestimmen deinen Startpunkt und gehen Schritt für Schritt zum nächsten Niveau.' },
      { t: 'DELE-Vorbereitung', d: 'Als DELE-Prüferin bereite ich dich auf alle vier Prüfungsteile mit Probeprüfungen und Strategien vor.' },
      { t: 'Zum Reisen, Arbeiten oder Leben in Spanien', d: 'Unterricht mit Fokus auf echte Situationen: Bewerbungen, Behörden, Meetings, Reisen und Alltag.' },
    ],
    ctaTitle: 'Beginne mit einer kostenlosen Probestunde',
    ctaMeta: ['30 Minuten', 'Online', 'Ohne Verpflichtung'],
    ctaButton: 'Kostenlose Probestunde buchen',
  },
};

// Enlaces internos a las páginas de nivel / DELE que ya existen.
const LINKS = [
  { to: '/espanol-a1', label: { es: 'Ver el nivel A1', en: 'See level A1', de: 'Niveau A1 ansehen' } },
  { to: '/espanol-b1', label: { es: 'Ver el nivel B1', en: 'See level B1', de: 'Niveau B1 ansehen' } },
  { to: '/espanol-a2', label: { es: 'Ver el nivel A2', en: 'See level A2', de: 'Niveau A2 ansehen' } },
  { to: '#niveles', label: { es: 'Ver todos los niveles', en: 'See all levels', de: 'Alle Niveaus ansehen' } },
  { to: '/preparacion-dele', label: { es: 'Preparación DELE', en: 'DELE preparation', de: 'DELE-Vorbereitung' } },
  null,
];

const META_ICONS = [Clock, Monitor, ShieldCheck];

export const HomeAudience = () => {
  const { language } = useLanguage();
  const copy = COPY[language] || COPY.es;
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const scrollToBooking = () => {
    trackTrialCtaClick('final_cta');
    const el = document.getElementById('booking');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="para-quien" className="py-20 md:py-24 bg-white relative overflow-hidden" data-testid="home-audience-section">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <span className="text-[#F5A6A4] font-semibold text-xs uppercase tracking-[0.18em]">{copy.kicker}</span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#4A4A4A] mt-4 mb-5 leading-tight" data-testid="home-audience-title">
            {copy.title}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-6" />
          <p className="text-base text-gray-600 leading-relaxed">{copy.lead}</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {copy.items.map((item, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={item.t}
                initial={{ opacity: 0, y: 24 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.55, delay: 0.1 + i * 0.08 }}
                whileHover={{ y: -6 }}
                data-testid={`home-audience-card-${i}`}
                className="group p-6 rounded-3xl bg-gradient-to-br from-[#F7FBFD] to-white border border-[#A8D8EA]/40 shadow-sm hover:shadow-xl transition-shadow"
              >
                <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#A8D8EA] to-[#7DBDD9] flex items-center justify-center mb-4 group-hover:rotate-6 transition-transform">
                  <Icon className="w-5 h-5 text-white" />
                </span>
                <h3 className="text-base font-bold text-[#4A4A4A] mb-2">{item.t}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.d}</p>
                {LINKS[i] && (() => {
                  const link = LINKS[i];
                  const text = link.label[language] || link.label.es;
                  const cls = 'mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#5DA6C9] hover:text-[#4A4A4A] transition-colors';
                  const inner = (
                    <>
                      {text}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </>
                  );
                  return link.to.startsWith('#') ? (
                    <a href={link.to} data-testid={`home-audience-link-${i}`} className={cls}>{inner}</a>
                  ) : (
                    <Link to={link.to} data-testid={`home-audience-link-${i}`} className={cls}>{inner}</Link>
                  );
                })()}
              </motion.div>
            );
          })}
        </div>

        {/* CTA antes de tarifas */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 rounded-3xl p-8 md:p-10 bg-gradient-to-r from-[#E8F5FA] via-white to-[#FFF0EF] border border-[#A8D8EA]/50 shadow-lg flex flex-col lg:flex-row items-center justify-between gap-8"
          data-testid="home-trial-cta"
        >
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-[#4A4A4A]">{copy.ctaTitle}</h3>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4">
              {copy.ctaMeta.map((m, i) => {
                const Icon = META_ICONS[i];
                return (
                  <span key={m} className="inline-flex items-center gap-2 text-sm text-gray-600">
                    <Icon className="w-4 h-4 text-[#7DBDD9]" />
                    {m}
                  </span>
                );
              })}
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={scrollToBooking}
            data-testid="home-trial-cta-btn"
            className="group flex-shrink-0 px-8 py-4 bg-[#7DBDD9] hover:bg-[#5DA6C9] text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
          >
            {copy.ctaButton}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};
